(function () {
  const ROOT = '[data-solena-quiz]';
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));

  class LightingQuiz {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-solena-quiz-dialog]');
      this.steps = [...root.querySelectorAll('[data-solena-quiz-step]')];
      this.questions = root.querySelector('[data-solena-quiz-questions]');
      this.loading = root.querySelector('[data-solena-quiz-loading]');
      this.results = root.querySelector('[data-solena-quiz-results]');
      this.grid = root.querySelector('[data-solena-quiz-result-grid]');
      this.footer = root.querySelector('[data-solena-quiz-footer]');
      this.previousButton = root.querySelector('[data-solena-quiz-previous]');
      this.nextButton = root.querySelector('[data-solena-quiz-next]');
      this.count = root.querySelector('[data-solena-quiz-count]');
      this.progress = root.querySelector('[data-solena-quiz-progress]');
      this.transcript = root.querySelector('[data-solena-quiz-transcript]');
      this.toast = root.querySelector('[data-solena-quiz-toast]');
      this.answers = {};
      this.index = 0;
      this.lastFocus = null;
      this.limit = Math.max(1, Number(root.dataset.recommendationCount) || 3);
      this.showAdd = root.dataset.showAddToCart === 'true';
      this.products = this.readProducts();
      this.bind();
      this.syncEntry();
      this.renderStep();
      root.__lightingQuiz = this;
    }

    readProducts() {
      try {
        const value = JSON.parse(this.root.querySelector('[data-solena-quiz-products]')?.textContent || '[]');
        return Array.isArray(value) ? value : [];
      } catch (error) {
        console.error('Invalid Solena quiz product JSON.', error);
        return [];
      }
    }

    syncEntry() {
      document.querySelectorAll('[data-solena-quiz-entry]').forEach((entry) => {
        const heading = entry.querySelector('h2');
        const description = entry.querySelector('p');
        const button = entry.querySelector('[data-solena-quiz-open]');
        if (heading && this.root.dataset.heading) heading.textContent = this.root.dataset.heading;
        if (description && this.root.dataset.description) description.textContent = this.root.dataset.description;
        if (button && this.root.dataset.buttonLabel) {
          button.innerHTML = `${escapeHtml(this.root.dataset.buttonLabel)} <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"></polyline></svg>`;
        }
      });
    }

    bind() {
      this.root.querySelector('[data-solena-quiz-close]')?.addEventListener('click', () => this.close());
      this.root.querySelector('[data-solena-quiz-retake]')?.addEventListener('click', () => this.reset());
      this.previousButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next());
      this.steps.forEach((step) => step.querySelectorAll('[data-value]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.addEventListener('click', () => this.select(step, button));
      }));
      this.dialog?.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog?.addEventListener('click', (event) => { if (event.target === this.dialog) this.close(); });
      this.dialog?.addEventListener('close', () => this.afterClose());
      this.dialog?.addEventListener('keydown', (event) => this.trapFocus(event));
      this.grid?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-solena-quiz-add]');
        if (button) this.addToCart(button);
      });
    }

    open(trigger) {
      this.lastFocus = trigger || document.activeElement;
      if (typeof this.dialog?.showModal === 'function') this.dialog.showModal();
      else this.dialog?.setAttribute('open', '');
      document.body.classList.add('solena-quiz-open');
      requestAnimationFrame(() => this.root.querySelector('[data-solena-quiz-close]')?.focus());
    }

    close() {
      clearTimeout(this.loadingTimer);
      if (typeof this.dialog?.close === 'function') this.dialog.close();
      else { this.dialog?.removeAttribute('open'); this.afterClose(); }
    }

    afterClose() {
      document.body.classList.remove('solena-quiz-open');
      this.lastFocus?.focus?.();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const controls = [...this.dialog.querySelectorAll('button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])')].filter((element) => !element.hidden && element.offsetParent !== null);
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    select(step, selected) {
      step.querySelectorAll('[data-value]').forEach((button) => {
        const active = button === selected;
        button.classList.toggle('is-selected', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.answers[step.dataset.key] = selected.dataset.value;
      this.renderStep();
    }

    renderStep() {
      this.steps.forEach((step, index) => { step.hidden = index !== this.index; });
      this.renderTranscript();
      const step = this.steps[this.index];
      const answered = Boolean(step && this.answers[step.dataset.key]);
      this.count.textContent = `${this.index + 1} of ${this.steps.length}`;
      this.progress.style.width = `${((this.index + 1) / this.steps.length) * 100}%`;
      this.previousButton.disabled = this.index === 0;
      this.nextButton.disabled = !answered;
      this.nextButton.querySelector('span').textContent = this.index === this.steps.length - 1 ? 'See My Matches' : 'Next';
    }

    renderTranscript() {
      if (!this.transcript) return;
      const avatar = escapeHtml(this.root.dataset.botAvatar || '');
      const userIcon = '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="3.5"></circle><path d="M5 20c.6-4 3-6 7-6s6.4 2 7 6"></path></svg>';
      this.transcript.innerHTML = this.steps.slice(0, this.index).map((step, index) => {
        const question = step.querySelector('h2')?.textContent?.trim() || '';
        const selected = step.querySelector(`[data-value="${this.answers[step.dataset.key]}"]`);
        if (!selected) return '';
        return `<div class="solena-quiz-message is-assistant is-history"><span class="solena-quiz-avatar"><img src="${avatar}" alt="" width="32" height="32"></span><div class="solena-quiz-bubble"><small>SOLENA GUIDE · QUESTION ${index + 1}</small><p>${escapeHtml(question)}</p></div></div><div class="solena-quiz-message is-user"><div class="solena-quiz-bubble"><small>YOUR ANSWER</small><p>${escapeHtml(selected.textContent.trim())}</p></div><span class="solena-quiz-avatar">${userIcon}</span></div>`;
      }).join('');
    }

    previous() {
      if (this.index > 0) { this.index -= 1; this.renderStep(); }
    }

    next() {
      const step = this.steps[this.index];
      if (!this.answers[step.dataset.key]) return;
      if (this.index < this.steps.length - 1) {
        this.index += 1;
        this.renderStep();
        requestAnimationFrame(() => {
          this.steps[this.index].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
          this.steps[this.index].querySelector('button')?.focus({ preventScroll: true });
        });
      } else this.findMatches();
    }

    score(product, sourceIndex) {
      const tags = new Set((product.tags || []).map((tag) => String(tag).toLowerCase()));
      const reasons = [];
      let score = 0;
      const copy = {
        'room-dining': 'Perfect for dining spaces', 'room-living': 'Made for welcoming living rooms', 'room-bedroom': 'Suited to calm bedrooms', 'room-entryway': 'A warm welcome for entryways',
        'style-organic': 'Matches your warm organic style', 'style-minimal': 'Complements modern minimal interiors', 'style-rustic': 'Adds natural rustic character', 'style-coastal': 'Fits a soft coastal palette',
        'material-rattan': 'Made with natural rattan', 'material-wood': 'Features natural wood', 'material-mixed': 'Combines rattan and wood texture',
        'priority-warm-light': 'Selected for warm ambient light', 'priority-handcrafted': 'Selected for handcrafted detail', 'priority-statement': 'Chosen for statement design', 'priority-versatile': 'Easy to style throughout your home'
      };
      if (tags.has(this.answers.room)) { score += 4; reasons.push(copy[this.answers.room]); }
      if (tags.has(this.answers.style)) { score += 3; reasons.push(copy[this.answers.style]); }
      if (this.answers.material !== 'no-preference' && tags.has(this.answers.material)) { score += 3; reasons.push(copy[this.answers.material]); }
      if (tags.has(this.answers.priority)) { score += 2; reasons.push(copy[this.answers.priority]); }
      return { ...product, score, reasons: reasons.filter(Boolean), sourceIndex };
    }

    recommendations() {
      const ranked = this.products.filter((product) => product?.available).map((product, index) => this.score(product, index)).sort((a, b) => b.score - a.score || a.sourceIndex - b.sourceIndex);
      const result = [];
      const ids = new Set();
      ranked.forEach((product) => {
        if (result.length < this.limit && !ids.has(String(product.id))) { ids.add(String(product.id)); result.push(product); }
      });
      return result;
    }

    findMatches() {
      this.questions.hidden = true;
      this.footer.hidden = true;
      this.results.hidden = true;
      this.loading.hidden = false;
      this.loadingTimer = setTimeout(() => {
        this.renderResults(this.recommendations());
        this.loading.hidden = true;
        this.results.hidden = false;
        this.results.querySelector('h2')?.focus({ preventScroll: true });
      }, 650);
    }

    renderResults(products) {
      if (!products.length) {
        this.grid.innerHTML = '<p class="solena-quiz-empty">No available lights were found. Choose a fallback collection in the Theme Editor.</p>';
        return;
      }
      this.grid.innerHTML = products.map((product) => {
        const reasons = product.reasons.length ? product.reasons.slice(0, 2) : ['A versatile favorite from our handcrafted collection'];
        const buy = !this.showAdd ? '' : Number(product.variantCount) === 1
          ? `<button type="button" data-solena-quiz-add data-variant-id="${escapeHtml(product.variantId)}">Add to Cart</button>`
          : `<a href="${escapeHtml(product.url)}">Choose Options</a>`;
        const image = product.image ? `<img src="${escapeHtml(product.image)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '';
        return `<article class="solena-quiz-card"><a class="solena-quiz-card__image" href="${escapeHtml(product.url)}">${image}</a><div class="solena-quiz-card__body"><h3>${escapeHtml(product.title)}</h3><span class="solena-quiz-card__price">${escapeHtml(product.price)}</span><ul>${reasons.map((reason) => `<li>${escapeHtml(reason)}</li>`).join('')}</ul><div class="solena-quiz-card__actions"><a href="${escapeHtml(product.url)}">View Product</a>${buy}</div></div></article>`;
      }).join('');
    }

    reset() {
      clearTimeout(this.loadingTimer);
      this.index = 0;
      this.answers = {};
      this.steps.forEach((step) => step.querySelectorAll('[data-value]').forEach((button) => { button.classList.remove('is-selected'); button.setAttribute('aria-pressed', 'false'); }));
      this.loading.hidden = true;
      this.results.hidden = true;
      this.questions.hidden = false;
      this.footer.hidden = false;
      this.renderStep();
      this.root.querySelector('.solena-quiz-main')?.scrollTo({ top: 0, behavior: 'smooth' });
    }

    async addToCart(button) {
      if (button.disabled) return;
      const label = button.textContent;
      button.disabled = true;
      button.textContent = 'Adding...';
      try {
        const base = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${base}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }] }) });
        if (!response.ok) throw new Error('Add failed');
        const cart = await fetch(`${base}cart.js`).then((value) => value.json());
        document.querySelectorAll('[data-lumi-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
        button.textContent = 'Added';
        this.showToast('Added to cart successfully.');
      } catch (error) {
        button.textContent = label;
        this.showToast('Unable to add this light. Please try again.');
      } finally {
        setTimeout(() => { button.disabled = false; if (button.textContent === 'Added') button.textContent = label; }, 1400);
      }
    }

    showToast(message) {
      this.toast.textContent = message;
      this.toast.classList.add('is-visible');
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2600);
    }

    destroy() {
      clearTimeout(this.loadingTimer);
      if (this.dialog?.open) this.close();
      delete this.root.__lightingQuiz;
    }
  }

  const initialize = (scope = document) => {
    const roots = scope.matches?.(ROOT) ? [scope] : [...(scope.querySelectorAll?.(ROOT) || [])];
    roots.forEach((root) => { if (!root.__lightingQuiz) new LightingQuiz(root); });
  };

  if (!window.__solenaQuizEvents) {
    window.__solenaQuizEvents = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-solena-quiz-open]');
      if (!trigger) return;
      const quiz = document.querySelector(ROOT)?.__lightingQuiz;
      if (quiz) { event.preventDefault(); quiz.open(trigger); }
    });
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => {
      const root = event.target.matches?.(ROOT) ? event.target : event.target.querySelector?.(ROOT);
      root?.__lightingQuiz?.destroy();
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();
})();
